import { getHiddenDataBoolean, getIsChina } from '../../../../domain/hidden-data-provider';
import {
    checkFacebookLoginStatus, facebookLogin,
    facebookLogOut,
    getUserData,
} from '../../../../domain/facebook/facebook-login-provider';
import { externalLogin, ILoginReferrer } from '../../dal/data-provider';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { DEFAULT_ERROR } from '../../../../DAL/data-provider';
import { redirectAfterLogin } from '../standard-login/login';
import { getPageNodeGUID } from '../../../../domain/common';
import { getUTMFromStorage } from '../../../../domain/marketing/utm/utm-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import {
    GOOGLE_ERROR,
    GOOGLE_SIGN_IN_INITIALIZED,
} from '../../../../domain/google/google-login-provider';
import { ExternalLoginServiceEnum } from '../../enums/external-service-enum';
import TextAboveLine from '../../../../ui/forms/text-above-line';
import FacebookIcon from '../../../../ui/svg/common/social-icons/facebook';
import {
    ExternalLoginResultStatusCode,
    IExternalLoginResult
} from '../../../../domain/external-login/external-login-types';
import { ILocalDataRequest } from '../../../shopping-cart/cart-bridger-pay/interfaces';
import { isIphone } from '../../../../domain/waves-apps/browser-detection';
import { getViewportWidth } from '../../../../domain/animations/animations-provider';
import { sendRegistrationEvents } from '../../../../domain/marketing/marketing-provider';

interface IExternalLogin {
    appCode?: string;
    ts?: number;
    uuid?: string;
    qs?: string;
    callback?: (response: { data: IServerValidation, statusCode: number } | null) => void;
    facebookText: string;
    googleTextParam: string;
    dividerText: string,
    isShoppingCart?: boolean;
    localData?: ILocalDataRequest;
    isAlignedLeft?: boolean;
}

const ExternalLogin = (props: IExternalLogin) => {

    const { facebookText, googleTextParam, dividerText } = props;

    const socialLoginEnabled = getHiddenDataBoolean('social-login-enabled') && !getIsChina();

    const [ error, setError ] = useState<string|ReactNode>('');

    const isAlignedLeft = props.isAlignedLeft ?? false;

    // ----------- GOOGLE --------------------

    const googlePlaceholderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const googleInitialized = () => {

            if(!googlePlaceholderRef || !googlePlaceholderRef.current || !window.google) return;

            const vw = getViewportWidth();

            // 408

            // https://developers.google.com/identity/gsi/web/reference/js-reference#google.accounts.id.renderButton
            window.google.accounts.id.renderButton(googlePlaceholderRef.current, {
                type: 'standard',
                size: 'large',
                width: Math.min(vw - 20*2 - 24*2, (props.isShoppingCart ? 350 : 320)),
                theme: 'filled_blue',
                // @ts-ignore
                text: googleTextParam,
                logo_alignment: 'left',
                locale: 'en_US',
            });

            // Ensure FedCM login works when available
            // window.google.accounts.id.prompt();
        };

        document.body.addEventListener(GOOGLE_SIGN_IN_INITIALIZED, googleInitialized);

        return () => {
            document.body.removeEventListener(GOOGLE_SIGN_IN_INITIALIZED, googleInitialized);
        };
    }, [
        googleTextParam,
        props.isShoppingCart,
    ]);

    useEffect(() => {
        document.body.dispatchEvent(new CustomEvent(GOOGLE_SIGN_IN_INITIALIZED));
    }, [
        error,
    ]);

    useEffect(() => {
        const googleError = (evt: CustomEvent) => {
            if(!evt || !evt.detail) return;
            handleServerError(evt.detail);
        };

        // @ts-ignore
        document.body.addEventListener(GOOGLE_ERROR, googleError);

        return () => {
            // @ts-ignore
            document.body.removeEventListener(GOOGLE_ERROR, googleError);
        };
    }, []);

    const facebookAfterLogin = async (accessToken: string, userID: string) => {
        if(!accessToken || !userID) {
            // setError(DEFAULT_ERROR);
            await facebookLogOut();
            return;
        }

        const data = await getUserData();
        const email = data?.email || '';
        const name = data?.name || '';

        if(!email) {
            setError('You must share your Facebook email with waves.com to login with Facebook.');
            await facebookLogOut();
            return;
        }

        const isPersistent = true;

        const utm = getUTMFromStorage();

        const response = await externalLogin(
            ExternalLoginServiceEnum.Facebook,
            accessToken,
            userID,
            email,
            name,
            props.isShoppingCart ? ILoginReferrer.CartExternalLogin : ILoginReferrer.MainExternalLogin,
            isPersistent,
            !!props.isShoppingCart,
            props.localData,

            props.appCode,
            props.ts || 0,
            props.uuid,
            props.qs,

            // message gears -------
            getPageNodeGUID(),
            utm ? utm.firstSource : '',
            utm ? utm.firstMedium : '',
            utm ? utm.firstCampaign : '',
            utm ? utm.lastSource : '',
            utm ? utm.lastMedium : '',
            utm ? utm.lastCampaign : '',
        );

        if(
            !response ||
            response.statusCode !== 200 ||
            !response.data.isValid){
            await facebookLogOut();
            handleServerError(response);
            return;
        }

        const isExistingUser = response?.data?.relatedData?.isExistingUser ?? false;

        if(!isExistingUser) {
            await sendRegistrationEvents();
        }

        // All is well -----------------
        if(!!props.callback && typeof props.callback === 'function') {
            props.callback(response);
        }
        else{
            redirectAfterLogin();
        }
    };

    // ------------  FACEBOOK ----------------
    const onFacebookLoginClick = async () => {

        if(isIphone()) {
            FB.login((loginResponse) => {

                let accessToken = '';
                let userID = '';

                if(loginResponse.status === 'connected' && loginResponse.authResponse) {
                    accessToken = loginResponse.authResponse.accessToken;
                    userID = loginResponse.authResponse.userID;
                }

                facebookAfterLogin(accessToken, userID);
            }, {
                // https://developers.facebook.com/docs/permissions
                scope: 'public_profile,email'
            });
        }
        else{
            const statusResponse = await checkFacebookLoginStatus();
            const isConnected = statusResponse.status === 'connected' && !!statusResponse.authResponse;

            let accessToken = '';
            let userID = '';

            if(isConnected) {
                accessToken = statusResponse.authResponse.accessToken;
                userID = statusResponse.authResponse.userID;
            }
            else{
                const loginResponse = await facebookLogin();
                if(loginResponse.status === 'connected' && !!loginResponse.authResponse) {
                    accessToken = loginResponse.authResponse.accessToken;
                    userID = loginResponse.authResponse.userID;
                }
            }

            await facebookAfterLogin(accessToken, userID);
        }
    };

    // ----------- COMMON --------------------

    const handleServerError = (response: { data: IServerValidation, statusCode: number }) => {

        const result : IExternalLoginResult|undefined = response?.data?.relatedData?.result;

        if(result && result.status === ExternalLoginResultStatusCode.DisabledUser) {
            setError(<span>Your account is inactive. Check your email for activation link, or contact <a href="/contact-sales" title="">Waves Customer Support</a>.</span>);
            return;
        }

        const errors = response?.data?.errors;

        if(errors && errors.length > 0){
            setError(errors.join(' '));
            return;
        }

        setError(DEFAULT_ERROR);
    };

    return (
        <>
            {
                socialLoginEnabled &&
                <div className="mb-6">

                    <div className={ `mb-6 text-sm ${ isAlignedLeft ? '' : 'text-center' }` }>By signing in with Google or Facebook I agree to the <a href="/legal/eula">End User License Agreement</a>, <a href="/legal/privacy-policy">Privacy Notice</a>, and <a href="/legal/waves-terms-of-use">Waves Terms of Use</a>.
                    </div>

                    <>
                        <div
                            className="google-extenral-login-ph mb-4 rounded overflow-hidden"
                            ref={ googlePlaceholderRef } />

                        <button
                            className="w-btn flex items-stretch"
                            style={{
                                background: '#4867AA',
                                borderRadius: '3px',
                                textAlign: 'left',
                                padding: '0 2px',
                                height: '40px',
                            }}
                            type="button"
                            onClick={ onFacebookLoginClick }>

                            <div className="bg-white rounded flex justify-center items-center" style={{
                                width: '36px',
                                margin: '2px 0',
                                borderRadius: '2px 0 0 2px',
                            }}>
                                <FacebookIcon
                                    width="12px"
                                    height="25px"
                                    color="#4867AA"
                                />
                            </div>
                            <span className="ml-4 flex-1 text-center font-normal text-tiny flex justify-center items-center">{ facebookText }</span>
                        </button>
                    </>

                    {
                        error &&
                        <div className="error-box text-sm mt-6" role="alert">{ error }</div>
                    }

                    <TextAboveLine text={ dividerText } />
                </div>
            }
        </>
    )
};

export default ExternalLogin;